import { useRef, useState, useEffect } from 'react';
import { EditorState } from 'draft-js';
import { RichUtils } from 'draft-js';
import {
    insertEmojiIntoEditor,
    makeBeforeInputHandler,
    makePasteHandler,
} from '../utils/editorUtils';

/**
 * useEditorField
 *
 * Manages everything for one Draft.js editor field:
 *   – editorState
 *   – emoji picker (open/close, cursor save, insert)
 *   – bold / italic / underline toggles
 *   – character limit (beforeInput + paste)
 *   – outside-click to close emoji picker
 *
 * @param {Object}  opts
 * @param {number}  opts.charLimit          Max characters allowed (default 100)
 * @param {boolean} opts.richText           Enable bold/italic/underline (default true)
 * @param {boolean} opts.clearStyleOnSpace  Clear inline style when user hits space (default false)
 *
 * @returns {Object} – everything the component needs, destructured below
 */
const useEditorField = ({
    charLimit         = 100,
    richText          = true,
    clearStyleOnSpace = false,
} = {}) => {
    const [editorState,      setEditorState]      = useState(EditorState.createEmpty());
    const [showEmojiPicker,  setShowEmojiPicker]  = useState(false);
    const [savedSelection,   setSavedSelection]   = useState(null);
    const [fieldErrors,      setFieldErrors]      = useState({});

    const emojiPickerRef = useRef(null);
    const editorRef      = useRef(null);

    // ── Outside-click: close emoji picker ────────────────────────────────────
    useEffect(() => {
        if (!showEmojiPicker) return;

        const handler = (e) => {
            if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
                setShowEmojiPicker(false);
                setSavedSelection(null);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showEmojiPicker]);

    // ── Editor change ─────────────────────────────────────────────────────────
    const handleEditorStateChange = (state) => {
        setEditorState(state);
        // Clear field-level error when user starts typing
        setFieldErrors((prev) => {
            if (!prev.content) return prev;
            const next = { ...prev };
            delete next.content;
            return next;
        });
    };

    // ── Rich-text toggles ─────────────────────────────────────────────────────
    const toggleBold      = () => setEditorState(RichUtils.toggleInlineStyle(editorState, 'BOLD'));
    const toggleItalic    = () => setEditorState(RichUtils.toggleInlineStyle(editorState, 'ITALIC'));
    const toggleUnderline = () => setEditorState(RichUtils.toggleInlineStyle(editorState, 'UNDERLINE'));

    const currentStyle = editorState.getCurrentInlineStyle();
    const isBold       = currentStyle.has('BOLD');
    const isItalic     = currentStyle.has('ITALIC');
    const isUnderline  = currentStyle.has('UNDERLINE');

    // ── Input / paste guards ──────────────────────────────────────────────────
    const handleBeforeInput = makeBeforeInputHandler(charLimit, clearStyleOnSpace, setEditorState);
    const handlePastedText  = makePasteHandler(charLimit, setEditorState);

    // ── Emoji ─────────────────────────────────────────────────────────────────
    const openEmojiPicker = () => {
        setSavedSelection(editorState.getSelection());
        setShowEmojiPicker((v) => !v);
    };

    const handleEmojiSelect = (emojiObj) => {
        const next = insertEmojiIntoEditor(editorState, emojiObj.emoji, savedSelection);
        setEditorState(next);
        setSavedSelection(null);
        if (editorRef.current) setTimeout(() => editorRef.current.focus(), 0);
    };

    // ── Helpers ───────────────────────────────────────────────────────────────
    const plainText  = editorState.getCurrentContent().getPlainText();
    const charCount  = [...plainText].length;
    const isOverLimit = charCount > charLimit;

    const resetEditor = () => {
        setEditorState(EditorState.createEmpty());
        setShowEmojiPicker(false);
        setSavedSelection(null);
        setFieldErrors({});
    };

    const validateContent = () => {
        if (!plainText.trim()) {
            setFieldErrors({ content: 'Content is required' });
            return false;
        }
        if (isOverLimit) {
            setFieldErrors({ content: `Content must not exceed ${charLimit} characters` });
            return false;
        }
        return true;
    };

    return {
        // State
        editorState,
        setEditorState,
        showEmojiPicker,
        fieldErrors,

        // Refs
        emojiPickerRef,
        editorRef,

        // Handlers
        handleEditorStateChange,
        handleBeforeInput,
        handlePastedText,
        handleEmojiSelect,
        openEmojiPicker,

        // Rich-text (only meaningful when richText=true)
        ...(richText && { toggleBold, toggleItalic, toggleUnderline, isBold, isItalic, isUnderline }),

        // Derived
        plainText,
        charCount,
        isOverLimit,
        charLimit,

        // Utilities
        resetEditor,
        validateContent,
    };
};

export default useEditorField;